import { AxiosError, InternalAxiosRequestConfig } from 'axios';

/**
 * Persistence and policy for the offline `send_form` queue.
 *
 * Kept apart from `api.tsx` so the rules below are testable without pulling in
 *   the axios instance, the store and the interceptors. `api.tsx` owns the
 *   wiring; this file owns the decisions.
 */

export const OFFLINE_REQUESTS_STORAGE_KEY = 'qrs';

export type QueuedFormRequest = {
  /**
   * Identifies the *intent*, not the attempt: this case and this form type.
   *   Keying on `form_type` alone — as this queue used to — meant an intake
   *   queued for one patient silently blocked an intake for the next one.
   */
  key: string;
  form_type: string;
  config: InternalAxiosRequestConfig;
};

/**
 * The case is already in the URL (`/cases/:id/send_form`), so the url plus the
 *   form type is the whole identity of the request.
 */
export const getRequestKey = (
  url: string | undefined,
  formType: string | undefined,
): string => `${url ?? ''}|${formType ?? ''}`;

/**
 * Rehydrates the queue, tolerating items written by an older build.
 *
 * Those carry `{ form_type, config }` and no `key`, and there is no schema
 *   version to check, so the key is derived when it is absent. Dropping them
 *   instead would throw away a therapist's pending request on deploy day.
 */
export const readQueue = (): QueuedFormRequest[] => {
  try {
    const raw = localStorage.getItem(OFFLINE_REQUESTS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter(item => item && item.config)
      .map(item => ({
        ...item,
        key: item.key ?? getRequestKey(item.config?.url, item.form_type),
      }));
  } catch {
    return [];
  }
};

/**
 * Persists the queue. Storage can be full, and this runs inside an axios
 *   request interceptor, where throwing would surface as the request's own
 *   failure and hide the real cause.
 */
export const writeQueue = (queue: QueuedFormRequest[]): void => {
  try {
    if (queue.length === 0) {
      localStorage.removeItem(OFFLINE_REQUESTS_STORAGE_KEY);
      return;
    }
    localStorage.setItem(OFFLINE_REQUESTS_STORAGE_KEY, JSON.stringify(queue));
  } catch (e) {
    console.error('Could not persist the offline form request queue', e);
  }
};

/** True when this exact intent is already waiting to be sent. */
export const isQueued = (
  queue: QueuedFormRequest[],
  key: string,
): boolean => queue.some(item => item.key === key);

/**
 * Whether a replay failure is the backend's final answer.
 *
 * A definitive answer means the request reached the backend and was rejected on
 *   its merits — an invalid form type, a template mismatch, a form already
 *   signed. Replaying it again would get the same answer, so it leaves the
 *   queue and the reason is shown.
 *
 * `401` is excluded: the response interceptor owns it, and by the time one
 *   surfaces here the session is being torn down, which is not the request's
 *   fault. `408` and `429` are excluded because they explicitly invite a retry.
 */
export const isDefinitiveFailure = (error: AxiosError): boolean => {
  const status = error?.response?.status;
  if (!status) return false;
  if (status === 401 || status === 408 || status === 429) return false;
  return status >= 400 && status < 500;
};

/**
 * Whether the request provably never left the device, which is the only case
 *   where replaying it again is certainly safe.
 *
 * The offline interceptor aborts before anything is sent, so a cancellation is
 *   proof of non-delivery. Everything else — a dropped connection, a timeout, a
 *   5xx — is an *unknown* outcome: the backend may well have created the
 *   document and lost the response on the way back.
 */
export const wasNeverSent = (error: AxiosError): boolean =>
  error?.code === 'ERR_CANCELED';
